import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { GradientTypography } from 'src/components/gradients/GradientTypography';
import { LoadingButton } from 'src/components/buttons/LoadingButton';
import { useState } from 'react';
import {
  WizardElementProps,
  withoutWizardProps,
} from 'src/components/wizard/Wizard';
import { useCheckIn } from 'src/components/tournaments/check-in/CheckInContext';
import { CheckInStore } from './useCheckInDialog';
import { useCheckInFunc } from './useCheckInFunc';
import { useAuth } from 'src/contexts/AuthContext';

export const CheckedInDialog = withoutWizardProps(
  ({ go }: WizardElementProps<CheckInStore>) => {
    const [isTeamCheckedIn, setIsTeamCheckedIn] = useState(false);
    const [error, setError] = useState(false);
    const { isCaptain, teammates } = useCheckIn();
    const { checkInFn } = useCheckInFunc();

    const { userData } = useAuth();
    const checkInEveryone = async () => {
      try {
        setError(false);
        //if the user has not updated their username, require them to do so
        if (!userData?.username || userData?.username?.length > 25) {
          go('CreateUsernameDialog');
          return;
        }
        await checkInFn(true);
        setIsTeamCheckedIn(true);
      } catch (e) {
        console.error(e);
        setError(true);
      }
    };

    const membersToCheckIn = teammates.filter((member) => {
      return member.status === 'accepted' && member.checkedIn === false;
    });
    const showTeamDetails = isCaptain && membersToCheckIn.length > 0;
    return (
      <>
        <DialogTitle
          sx={{
            color: 'black',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {
            <GradientTypography
              gradientColor="primary.vertical"
              variant="h4"
              sx={{ display: 'inline', textTransform: 'uppercase' }}
            >
              {isTeamCheckedIn && <span>Team</span>} Check-in Successful!
            </GradientTypography>
          }
        </DialogTitle>
        <DialogContent sx={{ maxWidth: 500, backgroundImage: 'none' }}>
          <Stack direction="column" spacing={2}>
            <Typography variant="h6">{`You've successfully checked in${
              isTeamCheckedIn ? ' everyone' : ''
            }!`}</Typography>
            {showTeamDetails && (
              <>
                <Typography variant="h6">
                  Would you like to check in everyone on your team?
                </Typography>

                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{ alignItems: 'center' }}
                >
                  <Typography
                    variant="subtitle1"
                    color="warning.light"
                    sx={{
                      my: -1,
                      textTransform: 'none',
                      textAlign: 'start',
                    }}
                  >
                    If any member on your team does not show up in time for your
                    first match, your entire team will be
                    <span style={{ fontWeight: 700, paddingLeft: 1 }}>
                      disqualified
                    </span>
                    .
                  </Typography>
                </Alert>
              </>
            )}
            {error && (
              <Alert severity="error" variant="outlined">
                <Typography
                  variant="subtitle1"
                  color="error.main"
                  sx={{ my: -1, textTransform: 'none' }}
                >
                  There was an error when trying to check-in your team.
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: 'center', width: '100%', mt: 4, pb: 0, px: 0 }}
        >
          {showTeamDetails ? (
            <Stack direction="row" sx={{ width: '100%' }}>
              <LoadingButton
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                onClick={checkInEveryone}
                sx={{ flex: 2, mr: 1 }}
              >
                Check-In Everyone
              </LoadingButton>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => {
                  return go(undefined);
                }}
                sx={{ flex: 1, ml: 1 }}
              >
                Close
              </Button>
            </Stack>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                return go(undefined);
              }}
              sx={{ flex: 1, maxWidth: 375 }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </>
    );
  },
);
